import React, { useEffect, useMemo, useRef, useState } from "react";

import { Panel } from "primereact/panel";
import { ProgressBar } from "primereact/progressbar";
import { buildRoute } from "../../../constants/routes";
import { useSmartNavigation } from "../../../helpers/navigation";
import { categorizeScores } from "../../../helpers/scores";
import { sanitizeTestId } from "../../../utils/string";

import S from "./SummaryTopicCard.module.scss";
import S2 from "./SummaryTopicCard.new.module.scss";
import {
  SummaryTopicCardHeaderTemplateProps,
  SummaryTopicCardProps,
  TopicGroup,
} from "./summaryTopicCardTypes";
import type { AssessmentLessonRecommendation } from "../../../client/svcPla";

import { LessonRecommendationCard } from "./LessonRecommendationCard";

export const SummaryTopicCard: React.FC<SummaryTopicCardProps> = ({
  categoryScore,
  topicScoresAverage,
  expanded,
  showLessonRecommendations = false,
}) => {
  const ref = useRef<Panel>(null);
  const categoryTopicScores = useMemo(
    () => topicScoresAverage[categoryScore.category] || [],
    [topicScoresAverage, categoryScore.category]
  );
  const { poorTopics, strongTopics, fairTopics } = useMemo(
    () => categorizeScores(categoryTopicScores),
    [categoryTopicScores]
  );
  const recommendationsByPath = useMemo<Record<string, AssessmentLessonRecommendation>>(
    () =>
      Object.fromEntries(
        categoryTopicScores.flatMap(({ topicId, lessonRecommendation }) =>
          topicId && lessonRecommendation ? [[topicId, lessonRecommendation]] : []
        )
      ),
    [categoryTopicScores]
  );
  const topicGroups = useMemo<TopicGroup[]>(
    () => [
      { id: "strong", title: "Strengths", topics: strongTopics, icon: "pi-check" },
      { id: "fair", title: "Proficiencies", topics: fairTopics, icon: "pi-minus" },
      {
        id: "poor",
        title: "Growth Opportunities",
        topics: poorTopics,
        icon: "pi-exclamation-triangle",
        showRecommendations: true,
      },
    ],
    [fairTopics, poorTopics, strongTopics]
  );
  const [topicScore, setTopicScore] = useState<number | null>(null);
  const { navigateTo } = useSmartNavigation();

  useEffect(() => {
    if (ref.current) {
      if (expanded) {
        ref.current.expand(undefined);
      } else {
        ref.current.collapse(undefined);
      }
    }
  }, [expanded]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setTopicScore(categoryScore.averageScore);
    }, 500);

    return () => window.clearTimeout(timeoutId);
  }, [categoryScore.averageScore]);

  const handleLessonStart = (lessonKey: string) => {
    navigateTo(buildRoute("Learn.routes.lesson.route", { lessonKey }));
  };

  return (
    <Panel
      ref={ref}
      toggleable
      collapsed={true}
      className={showLessonRecommendations ? S2.panel : undefined}
      expandIcon="pi pi-angle-left"
      collapseIcon="pi pi-angle-down"
      header={categoryScore.category}
      data-testid={`summary-topic-card-${sanitizeTestId(categoryScore.category)}`}
      headerTemplate={props => (
        <SummaryTopicCardHeaderTemplate
          {...props}
          topicScore={topicScore}
          category={categoryScore.category}
        />
      )}
      pt={{
        content: {
          className: showLessonRecommendations ? S2.categoryContent : S.categoryContent,
        },
      }}
    >
      {showLessonRecommendations ? (
        <LessonRecommendationCard
          groups={topicGroups}
          recommendationsByPath={recommendationsByPath}
          onLessonStart={handleLessonStart}
        />
      ) : (
        <LegacyTopicGroups groups={topicGroups} />
      )}
    </Panel>
  );
};

const LegacyTopicGroups: React.FC<{ groups: TopicGroup[] }> = ({ groups }) => (
  <div className={S.topicWrapper}>
    {groups
      .filter(group => group.topics.length > 0)
      .map(group => (
        <div key={group.id} className={S.topicContent}>
          <div className="body-sm text-surface-400">{group.title}</div>
          {group.topics.map(topicScore => (
            <div key={topicScore.topicId ?? topicScore.topic} className={S.topicItem}>
              <i className={`pi ${group.icon}`} />
              {topicScore.topic}
            </div>
          ))}
        </div>
      ))}
  </div>
);

const SummaryTopicCardHeaderTemplate: React.FC<SummaryTopicCardHeaderTemplateProps> = ({
  onTogglerClick,
  titleElement,
  togglerClassName,
  togglerElement,
  topicScore,
  category,
}) => {
  const score = topicScore ?? 0;

  return (
    <div className={S.categoryHeader} onClick={onTogglerClick}>
      <div className={S.categoryTitle}>
        <h2 className="atlas-h5">{titleElement}</h2>
        <div className={togglerClassName}>{togglerElement}</div>
      </div>
      <ProgressBar
        value={score}
        color="var(--indigo-dataviz-2)"
        aria-label={`${category} score: ${score}%`}
        pt={{ label: { className: "text-surface-0" }, root: { className: S.progressContainer } }}
      />
    </div>
  );
};
